/**
 * Overlay system — scrims, panels, banners, and dialog actions (plain objects for inline `style={}`).
 * Aligns with semantic tokens; no blur/glass. Layering z-index: use `overlayLayers` at call sites.
 */

import { foundation as f } from './tokens';
import { OVERLAY_MOTION } from './motion/overlayMotionTokens';
import { components } from './theme/componentTokens';
import { bg, fg } from './theme/semanticTokens';
import {
  MODAL_SAFE_MAX_HEIGHT,
  MODAL_COMPACT_MAX_HEIGHT,
  MODAL_LARGE_MAX_HEIGHT,
  MODAL_LARGE_MAX_HEIGHT_MOBILE,
  MODAL_TOUCH_BAND_MAX_HEIGHT,
  mobileFullscreenModalSizing,
  MODAL_TOP_RESERVE_VAR,
  MODAL_BOTTOM_RESERVE_VAR,
} from './modalSizeTokens';

const { color, spacePx, radii, shadow, fontSize, fontWeight, lineHeight, fontFamily } = f;
const scrim = components.overlay.scrim;

// Modal HEIGHT tokens live in ./modalSizeTokens (500-line cap) — imported
// above (this file uses MODAL_COMPACT_MAX_HEIGHT etc. below) AND re-exported
// here so every existing `import { MODAL_COMPACT_MAX_HEIGHT } from
// '.../overlayStyles'` keeps working unchanged. Import directly from
// ./modalSizeTokens in new code if you don't also need anything else here.
export {
  MODAL_SAFE_MAX_HEIGHT,
  MODAL_COMPACT_MAX_HEIGHT,
  MODAL_LARGE_MAX_HEIGHT,
  MODAL_LARGE_MAX_HEIGHT_MOBILE,
  MODAL_TOUCH_BAND_MAX_HEIGHT,
  mobileFullscreenModalSizing,
  MODAL_TOP_RESERVE_VAR,
  MODAL_BOTTOM_RESERVE_VAR,
};

/**
 * Full-viewport scrim + flex center (modals, confirmations).
 *
 * Anchored top/bottom (not `height: 100dvh`): in iOS Safari browser mode the
 * layout viewport is `100lvh` (taller than the visible `100dvh` by the URL-bar
 * height), and `position: fixed` resolves against the layout viewport. A
 * `top: 0; height: 100dvh` scrim ends URL-bar-height above the visible bottom,
 * leaking page content beneath the modal. `top/bottom: 0` fills the layout
 * viewport — the overflow sits harmlessly behind the URL bar.
 */
export const modalScrimCentered = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  // WebKit bottom-strip guard (Safari; Chrome unaffected). NOT scrollbar-related
  // (the strip persists with macOS scrollbars off). html/body are
  // `height:100dvh; min-height:100lvh` so the document renders at max(dvh,lvh);
  // the scrim sized to a single unit (lvh=836) fell short of the document (~870)
  // and the canvas showed through as a strip. max() takes the taller = the
  // document's own height, so the dim matches it. `top + height` win over bottom:0.
  height: 'max(100dvh, 100lvh)',
  backgroundColor: scrim,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  // Wider horizontal gutter (24px) than vertical (16px) so modals never read
  // edge-to-edge on phones (the "modals too wide on mobile" report). 24px L/R is
  // the canonical modal side-gutter — `computeKeyboardOverlayStyles` mirrors it so
  // the modal's width stays IDENTICAL when the keyboard reframes the scrim.
  // Vertical padding carries the safe-area inset ON TOP of the 16px gutter, so a
  // centered card is centered within the SAFE box rather than the raw viewport.
  // Without this the panel's own maxHeight is not enough: the scrim centers in
  // the full height, so half the leftover lands above the card and a tall modal
  // still tucks its title under the Dynamic Island (observed on device).
  // env() resolves to 0px off-device, so non-notched screens are byte-identical.
  //
  // max(env(...), var(--cvx-modal-*-reserve, 0px)) (founder standing order,
  // 2026-09-27): on the touch shell, `useModalTouchReserves` publishes the
  // rendered header's bottom edge / the bottom nav pill's top edge onto
  // <html> as `--cvx-modal-top-reserve` / `--cvx-modal-bottom-reserve` — a
  // reserve BIGGER than the raw safe-area inset (the usual case: a header/pill
  // is taller than the notch/home-indicator) wins, so the CENTERED resting
  // card is centered within the band between them, not just clear of the
  // hardware. The var is unset (0px) off the touch shell (desktop, login,
  // splash), where max() always resolves to the env() term — byte-identical
  // to before.
  paddingTop: `calc(${spacePx[4]} + max(env(safe-area-inset-top, 0px), var(${MODAL_TOP_RESERVE_VAR}, 0px)))`,
  // + max(0px, 100lvh - 100dvh) (2026-09-27, safari-top-and-centering):
  // the scrim box above is sized to `max(100dvh, 100lvh)` (the WebKit
  // bottom-strip guard), which in Safari BROWSER is taller than the
  // visible area by the hidden URL-bar sliver (lvh 836 vs dvh 796 on the
  // founder's device, ~40px). `alignItems: center` then centers the panel
  // within that taller box, landing it ~half the sliver (~20pt) BELOW the
  // true visible center. Adding the sliver to the bottom padding shifts
  // the flex-centered content back up by half of it, restoring true
  // centering. 0 wherever lvh === dvh (installed PWA, desktop); in an
  // Android Chrome tab it is the URL-bar height and the same fix applies.
  paddingBottom: `calc(${spacePx[4]} + max(env(safe-area-inset-bottom, 0px), var(${MODAL_BOTTOM_RESERVE_VAR}, 0px)) + max(0px, 100lvh - 100dvh))`,
  paddingLeft: spacePx[6],
  paddingRight: spacePx[6],
  boxSizing: 'border-box',
  transition: 'opacity 200ms cubic-bezier(0.16, 1, 0.3, 1)',
};

/** Nav / slide-over backdrop (opacity toggled by open/closed classes) */
export const drawerScrimBackdrop = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: scrim,
  transition: 'opacity 200ms cubic-bezier(0.16, 1, 0.3, 1)',
};

/** Full-screen form drawer scrim */
export const formDrawerScrim = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: scrim,
  transition: 'opacity 320ms cubic-bezier(0.16, 1, 0.3, 1)',
};

/** Shared slide-drawer motion — keep scrim + panel in sync. */
export const drawerMotion = {
  // The overlay "slide" family (RD-UI-192, src/ui/overlayMotion.js): 300ms in, 160ms out.
  durationMs: { mobile: OVERLAY_MOTION.slideEnterMs, desktop: OVERLAY_MOTION.slideEnterMs },
  exitMs: OVERLAY_MOTION.slideExitMs,
  easing: OVERLAY_MOTION.easing,
};

export const modalDialogPanel = {
  width: '100%',
  maxWidth: '480px',
  // dvh (not vh): clamp to the *current* visible viewport so the modal can
  // never exceed the area the user actually sees when iOS shows the URL bar.
  //
  // The shared compact-family rule — proportion + hardware + desktop ceiling.
  // min() throughout, so this can only ever make the card SMALLER, never larger,
  // which is what makes it safe under the many modals that inherit it.
  maxHeight: MODAL_COMPACT_MAX_HEIGHT,
  overflow: 'hidden',
  backgroundColor: color.neutral.surfaceElevated,
  borderRadius: radii['2xl'],
  border: `1px solid ${color.neutral.border}`,
  boxShadow: shadow.md,
  padding: spacePx[5],
  boxSizing: 'border-box',
  transition: 'opacity 200ms cubic-bezier(0.16, 1, 0.3, 1), transform 200ms cubic-bezier(0.34, 1.56, 0.64, 1)',
};

export const confirmDialogPanel = {
  ...modalDialogPanel,
  maxWidth: '420px',
};

export const drawerSheetRight = {
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  width: 'min(100%, 420px)',
  backgroundColor: color.neutral.surface,
  borderLeft: `1px solid ${color.neutral.border}`,
  boxShadow: shadow.md,
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
};

// The banner helpers (bannerDismissButton, bannerToneColor) live in `./bannerStyles`;
// notices themselves render through `CalmBanner` (KAN-1173). Split out to keep this
// module under the 500-line cap. Import those from there directly; this file
// deliberately does NOT re-export them, so the two modules stay acyclic
// (same pattern as `./confirmDialogStyles` below).

export const overlayActionsRow = {
  display: 'flex',
  gap: spacePx[3],
  flexWrap: 'wrap',
};

// The confirm / typed-confirm family (dialog shell, pinned type-to-confirm
// field, impact card) lives in `./confirmDialogStyles` — extracted to keep this
// module under the 500-line cap. Import those from there directly; this file
// deliberately does NOT re-export them, so the two modules stay acyclic
// (`confirmDialogStyles` imports `overlayActionsRow` from here).

/** Flex column shell for scrollable modals (header + body + footer). */
export const modalFlexColumnShell = {
  ...modalDialogPanel,
  display: 'flex',
  flexDirection: 'column',
};

export const modalPinnedSection = {
  flexShrink: 0,
};

export const modalBodyScrollRegion = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  WebkitOverflowScrolling: 'touch',
  // Stop the scroll CHAINING to the page. Without this, reaching either end of
  // a bounded modal body hands the gesture to the ancestor — and `bodyScrollLock`
  // has pinned html/body to `overflow: hidden`, so iOS meets a locked scroller
  // mid-fling. Momentum dies against it and the pane reads as sticky or
  // rubber-bandy right where a long form needs to feel smoothest.
  //
  // `DrawerShell` already contains its own scroll; the ~50 modals that spread
  // this token did not, so drawers and modals behaved differently for no
  // reason. One rule for both. Guard: overlayScrollContainment.test.js
  overscrollBehavior: 'contain',
};

/** Full-width reason rows for no-service pickers (6 options; one may hint a date). */
export const noServiceReasonList = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  marginBottom: '16px',
};

export const modalPinnedFooterRow = {
  ...overlayActionsRow,
  // Never grow: ActionBeatFill's row defaults to `flex: 1 1 100%` (meant for a sideways actions row).
  // As a direct child of a column-flex panel whose height is pinned (keyboard up), that basis took
  // the panel's full height and squeezed the scroll body to nothing (KAN-861, iPhone 2026-10-21).
  flex: '0 0 auto',
  flexShrink: 0,
  marginTop: spacePx[4],
};

// Footer action buttons keep their labels on ONE line (`whiteSpace: nowrap`).
// `flex: '1 1 auto'` (vs the old `flex: 1` = `1 1 0`, equal 50/50) lets each
// button size to its content and grow to fill the row, so a long primary label
// (e.g. Spanish "Crear borrador de factura") gets the width it needs and stays
// side-by-side on one line instead of being forced into ~50% and wrapping to two
// rows. `overlayActionsRow`'s `flexWrap: wrap` remains only as an extreme-case
// safety (pathologically long label on the smallest phone) — it stacks rather
// than clip/overflow; in practice buttons stay side-by-side.
export const overlayPrimaryAction = {
  flex: '1 1 auto',
  whiteSpace: 'nowrap',
  padding: `${spacePx[3]} ${spacePx[4]}`,
  background: color.brand.primary,
  color: color.brand.onPrimary,
  border: `1px solid ${color.brand.primary}`,
  borderRadius: radii.md,
  cursor: 'pointer',
  fontWeight: fontWeight.semibold,
  fontSize: fontSize.sm,
  fontFamily: fontFamily.sans,
  boxShadow: shadow.xs,
};

export const overlaySecondaryAction = {
  flex: '1 1 auto',
  whiteSpace: 'nowrap',
  padding: `${spacePx[3]} ${spacePx[4]}`,
  backgroundColor: color.neutral.surfaceMuted,
  color: color.neutral.text,
  border: `1px solid ${color.neutral.border}`,
  borderRadius: radii.md,
  cursor: 'pointer',
  fontWeight: fontWeight.semibold,
  fontSize: fontSize.sm,
  fontFamily: fontFamily.sans,
};

/**
 * Quiet text footer action — a dismissive/secondary control (e.g. "Later",
 * "Cancel") that reads as a plain label, not a button: no fill, no border.
 * `flex: '0 0 auto'` (vs `overlaySecondaryAction`'s `1 1 auto`) so it sizes
 * to its own text and leaves the row's remaining width to the primary action
 * beside it (Office Inbox / row-pricing redesign, RD-ROLE-006 polish).
 */
export const overlayQuietAction = {
  flex: '0 0 auto',
  whiteSpace: 'nowrap',
  padding: `${spacePx[3]} ${spacePx[4]}`,
  backgroundColor: 'transparent',
  color: color.neutral.textSecondary,
  border: 'none',
  borderRadius: radii.md,
  cursor: 'pointer',
  fontWeight: fontWeight.semibold,
  fontSize: fontSize.sm,
  fontFamily: fontFamily.sans,
};

export const overlayDangerAction = {
  ...overlayPrimaryAction,
  background: color.semantic.dangerFill,
  backgroundColor: color.semantic.dangerFill,
  // Full `border` shorthand, not `borderColor`. The disabled spread also sets
  // `border`; a leftover `borderColor` longhand is applied after it and paints
  // a red ring on the muted button the moment the dialog opens (void, decline).
  border: `1px solid ${color.semantic.danger}`,
  color: color.brand.onPrimary,
};

// Canonical disabled treatment for overlay action buttons. Inline styles can't
// express `:disabled`, so a filled primary/danger button that is functionally
// `disabled` still LOOKS enabled (dark, tappable) — spread this over the base
// action style when disabled so the button reads muted + not-allowed. Reused by
// every modal whose confirm is gated until the form is valid (reschedule/skip,
// etc.), so "disabled until you pick a reason/date" is visible, not just inert.
export const overlayActionDisabled = {
  background: color.neutral.surfaceMuted,
  backgroundColor: color.neutral.surfaceMuted,
  color: color.neutral.textMuted,
  // Full `border` shorthand (not just `borderColor`): the filled primary sets
  // `border: 1px solid brand.primary`, and React inline styles will not let a
  // later `borderColor` beat that shorthand — the navy ring stays on a muted
  // disabled Save. `outline: none` drops the UA focus ring that otherwise
  // appears when Save flips enabled→disabled after an edit.
  border: `1px solid ${color.neutral.border}`,
  outline: 'none',
  cursor: 'not-allowed',
  boxShadow: 'none',
  opacity: 0.7,
};

// Canonical modal title: lg + BOLD. Modal headings are bold app-wide (the admin
// confirm dialogs, activity export, imports, and platform confirms already were)
// so every modal's header reads with the same weight — this is the single source
// of truth for the ~25 modals that spread `overlayTitle`.
export const overlayTitle = {
  margin: '0 0 8px 0',
  color: color.neutral.text,
  fontSize: fontSize.lg,
  fontWeight: fontWeight.bold,
  fontFamily: fontFamily.sans,
};

export const overlayBody = {
  color: color.neutral.textSecondary,
  fontSize: fontSize.md,
  lineHeight: lineHeight.relaxed,
  margin: '0 0 24px 0',
  whiteSpace: 'pre-line',
};

/** Flex header row inside drawers (title + close button) */
export const drawerHeaderBar = {
  padding: `${spacePx[5]} ${spacePx[6]}`,
  borderBottom: `1px solid ${color.neutral.border}`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: color.neutral.bgSubtle,
  flexShrink: 0,
  boxSizing: 'border-box',
};

/** Scrollable content area inside drawers */
export const drawerContentArea = {
  padding: spacePx[5],
  overflowY: 'auto',
  flex: 1,
  // Without minHeight:0 a flex:1 column child won't shrink below its content,
  // so it overflows the bounded drawer and clips instead of scrolling. Matches
  // `modalBodyScrollRegion`. (Currently no live consumer — correct-by-default.)
  minHeight: 0,
  // Same containment + momentum as DrawerShell's own scroller and
  // modalBodyScrollRegion — every bounded scroll area in the app behaves the
  // same way, whichever token a surface happens to reach for.
  WebkitOverflowScrolling: 'touch',
  overscrollBehavior: 'contain',
  display: 'flex',
  flexDirection: 'column',
  gap: spacePx[5],
};

/**
 * Canonical sign-out button — full-width navy CTA used on the non-dismissible
 * access screens (ProtectedRoute access-restricted / inactive-tenant) and the
 * MFA enrollment gate. Centered label, 8px radius, 600 weight. `color.neutral.text`
 * is the same navy (#0f172a) those screens hardcode; token-based so it tracks the
 * design system.
 */
export const signOutButtonStyle = {
  width: '100%',
  padding: `${spacePx[3]} ${spacePx[4]}`,
  fontSize: fontSize.sm,
  fontWeight: fontWeight.semibold,
  fontFamily: fontFamily.sans,
  color: fg.onInkFill,
  backgroundColor: bg.inkFill,
  border: 'none',
  borderRadius: radii.sm,
  cursor: 'pointer',
  textAlign: 'center',
  boxSizing: 'border-box',
};

